// 剣舞の役者の速い連・左右交互に「切りつける」止め絵（キーの姿勢）12枚（2026-11-04）。
//   作者（11-04 answers/blade-quick-dance-mockup-1104）：motion=redo。原文「ぐるぐる腕を回転させて攻撃してるように見える／左右交互に切りつけるようにしないと」。
//   前の3つの模型（blade_quick_alt・body・dance）は、2打ごとに腕と剣の角度へ 360 を足して同じ向きに回していた。
//   測ると1打ごとに手が肩の周りを約 360° 回っていた（＝風車）。docs/research/2026-11-04-blade-quick-cross-keyposes.md。
//   この版は動かす前に止め絵だけで決める：1発ごとに「振りかぶり→斬り抜け」の2枚、4発で8枚。
//   作者（11-04 answers/blade-quick-cross-keys-1104 stills=fix）：原文「右切り下げ、左切り下げ、右斬り上げ、左切り下げにして、
//     前に進みながら切れば踊ってるように切るモーションが作りやすいと思う」。
//     ①右（P・手前の剣）で切り下げ：体の後ろ上→前下。②左（Q・奥の剣）で切り下げ：頭の上→前下（①より低く）。
//     ③右で斬り上げ：①で振り抜いた前下から、戻さずそのまま前上へ振り上げる（ここで流れが変わる）。
//     ④は 11-04 3回目の答えで左の斬り上げにした（下）。
//     振り抜いた腕はその場に残し（戻さない）、次の打ちは反対の腕が始める。
//     1発ごとに斬る側の足を前へ踏み出し、4発で前へ進む。
//     踊りっぽさは腕の通り道を濁さない所で足す：切り下げは振りかぶりでつま先へ伸び上がり斬り抜けで沈む。
//     斬り上げは逆に、溜めで沈み斬り上げでつま先へ伸び上がる。合図の頭上の交差は1発目の振りかぶりが兼ねる。
//   作者（11-04 answers/blade-quick-cross-keys2-1104 stills=fix）：原文「前後逆になってない？あと、次振る刀は少しひくというか
//     若干の溜めをいれるといい感じになると思う。直線的な動きではないということ」。
//     次に振る刀は、振る向きと逆へ一度少し引いて（溜め）から振る：1発ごとに「振りかぶり→溜め→斬り抜け」の3枚、4発で12枚。
//     斬り抜けの手は肩の前をまっすぐ横切らず、弧の中ほど（via）を通る。「前後逆」：②④が体の後ろ下へ振り抜けていた（相手に背を向けて斬る形）ので、前下（相手の側）で抜き、③の斬り上げも頭の後ろでなく前上で止める。
//   作者（11-04 answers/blade-quick-cross-keys3-1104 flip=ok stills=fix）：原文「左が２回切り下げになっているから２回目は切り上げてほしい」。
//     1回目の「④左切り下げ」より今回の言葉を優先し、④を左の斬り上げにする（右下・左下・右上・左上）。
//     ④は③と左右を入れ替えた形：②で前下へ振り抜いた左手を戻さず低いまま、溜めで少し引き下げてから前上へ振り上げる。
//   座標は模型の空間（x は前・y は負が上）。手の位置は肩からのずれで決め、二の腕・前腕は解いて出す。

import { HUMAN_RIG, humanPose, type HumanRig } from '../body'
import type { MockupMove, Pose, Vec } from '../core'

/** 腕：手の位置（肩から）と剣の向き（度・0＝前・90＝下）。bend＝肘を出す側（1／-1） */
export interface CrossArm { hand: Vec; s: number; bend: 1 | -1; via?: Vec }
type Body = Omit<HumanRig, 'aF' | 'bF' | 'sF' | 'aB' | 'bB' | 'sB'>
export interface CrossKey { label: string; hit: number; phase: 'wind' | 'pull' | 'cut'; side: 'F' | 'B'; dir: 'down' | 'up'; body: Body; F: CrossArm; B: CrossArm }

// 腕の形。手が肩の前を斜めに大きく渡り、剣は P で時計回りに 125°、Q で反時計回りに 140° だけ振る（回し続けない）。
// 斬り上げは P の振り抜きから少し沈めて溜め（pLow）、反時計回りに前上へ振り上げる（pHigh）
export const CROSS_ARM = {
  pWind: { hand: [-26, -28], s: 255, bend: 1 },  // 手前の手を頭の後ろ上へ・剣はほぼ真上（少し後ろ）
  pPull: { hand: [-33, -21], s: 238, bend: 1 },  // ①の溜め：手をもう少し頭の後ろへ引き、剣先を後ろへ倒す
  pCut: { hand: [32, 22], s: 380, bend: -1, via: [12, -36] },  // 前下へ斬り抜けた所・剣は前やや下。手は頭の前上を回って下りる
  qWind: { hand: [12, -32], s: 300, bend: 1 },   // 奥の手を頭の前上へ・剣は前上へ
  qPull: { hand: [4, -40], s: 282, bend: 1 },   // ②の溜め：手を前上へ少し引き上げ、剣先を後ろへ起こす（振りと逆回り）
  qCut: { hand: [14, 32], s: 440, bend: -1, via: [30, -6] },  // 前下へ斬り抜けた所・剣先は前やや下（相手の側）。手は体の前へふくらんで下りる
  pLow: { hand: [28, 30], s: 420, bend: -1 },    // 斬り上げの振りかぶり：振り抜いた手をそのまま低く・剣先は前下
  pLowPull: { hand: [-4, 10], s: 458, bend: -1 }, // ③の溜め：手を腰の横まで後ろへ引き、剣先を真下より少し後ろへ倒す（11-04 模型のチェック「引くより下ろすに見える」「剣先が床をこする」）
  pHigh: { hand: [26, -30], s: 300, bend: 1, via: [44, 2] },  // 斬り上げ抜けた所：手は頭の前上・剣は前上（相手の側で振り上げる）。手は体の前へふくらんで上がる
  qLow: { hand: [18, 30], s: 440, bend: -1 },    // ④の振りかぶり：②で振り抜いた奥の手をそのまま低く・剣先は前下
  qLowPull: { hand: [-10, 8], s: 470, bend: -1 }, // ④の溜め：手を腰の後ろへ引き、剣先を後ろ下へ倒す（11-04 模型のチェック「垂れているだけで引きが無い」「振り出しで剣が脚と床を通る」）
  qHigh: { hand: [18, -34], s: 310, bend: 1, via: [40, 0] },  // ④の斬り上げ抜けた所：奥の手は頭の前上・剣は前上。③の右手より少し上・内で抜ける
} satisfies Record<string, CrossArm>
const A = CROSS_ARM as Record<keyof typeof CROSS_ARM, CrossArm>

// 足：手前の足が1・3発目、奥の足が2・4発目で前へ出る（歩幅 18・最初は踏み込みで 38）
// 1発ごとに斬る側の足を、もう片方の足の前へ大きく踏み出す（右・左・右・左と歩くように4歩で約 1.5 体ぶん前へ）
const FEET = { f0: 6, b0: -4, f1: 40, b2: 76, f3: 112, b4: 148 }
const mid = (a: number, b: number) => (a + b) / 2
const pxOf = (rear: number, front: number) => rear + 0.62 * (front - rear)
const wind = (o: Partial<Body>): Body => ({ px: 0, py: -77, lean: -2, tw: 0, ht: 0, fF: [0, 0], fB: [0, 0], rF: 0.6, rB: 0.6, ...o })
// 溜め：振りかぶりの体から、斬る側の肩をもう少し引いて（ひねりを 1.25 倍）わずかに伸び上がり反る（斬り上げは呼ぶ側で沈ませる）
const pull = (b: Body, o: Partial<Body> = {}): Body => ({ ...b, tw: b.tw * 1.25, py: b.py - 2, lean: b.lean - 3, ...o })
const cut = (o: Partial<Body>): Body => ({ px: 0, py: -65, lean: 13, tw: 0, ht: 0, fF: [0, 0], fB: [0, 0], rF: 0, rB: 0, ...o })

/** 12枚。1発ごとに振りかぶり→溜め（振る向きと逆へ少し引く）→斬り抜け。切り下げは振りかぶりで斬る側の肩を引いて伸び上がり、斬り抜けで踏み着いて沈む。斬り上げは溜めで沈み、斬り上げで伸び上がる */
const W1 = wind({ px: -2, tw: -34, ht: -10, fF: [mid(FEET.f0, FEET.f1), -7], fB: [FEET.b0, 0], rF: 1, rB: 0.8 })
const W2 = wind({ px: pxOf(FEET.b0, FEET.f1) + 4, tw: 34, ht: 12, fF: [FEET.f1, 0], fB: [mid(FEET.b0, FEET.b2), -7], rF: 0.7, rB: 1 })
const W3 = cut({ px: pxOf(FEET.f1, FEET.b2) + 3, py: -60, lean: 17, tw: 30, ht: 14, fF: [mid(FEET.f1, FEET.f3) - 6, -6], fB: [FEET.b2, 0], rF: 1, rB: 0.2 })
// ④は③の左右を入れ替えた体：振りかぶりで低く構え、溜めで沈み、斬り上げで伸び上がる
const W4 = cut({ px: pxOf(FEET.b2, FEET.f3) + 3, py: -60, lean: 17, tw: -30, ht: -14, fF: [FEET.f3, 0], fB: [mid(FEET.b2, FEET.b4) - 6, -6], rF: 0.2, rB: 1 })
export const BLADE_QUICK_CROSS_KEYS: CrossKey[] = [
  { label: '1発目・右の切り下げ・振りかぶり', hit: 1, phase: 'wind', side: 'F', dir: 'down', body: W1, F: A.pWind, B: A.qWind },
  { label: '1発目・右の切り下げ・溜め', hit: 1, phase: 'pull', side: 'F', dir: 'down', body: pull(W1), F: A.pPull, B: A.qWind },
  { label: '1発目・右の切り下げ・斬り抜け', hit: 1, phase: 'cut', side: 'F', dir: 'down',
    body: cut({ px: pxOf(FEET.b0, FEET.f1), tw: 28, ht: 20, fF: [FEET.f1, 0], fB: [FEET.b0, 0], rB: 0.5 }), F: A.pCut, B: A.qWind },
  { label: '2発目・左の切り下げ・振りかぶり', hit: 2, phase: 'wind', side: 'B', dir: 'down', body: W2, F: A.pCut, B: A.qWind },
  { label: '2発目・左の切り下げ・溜め', hit: 2, phase: 'pull', side: 'B', dir: 'down', body: pull(W2), F: A.pCut, B: A.qPull },
  { label: '2発目・左の切り下げ・斬り抜け', hit: 2, phase: 'cut', side: 'B', dir: 'down',
    body: cut({ px: pxOf(FEET.f1, FEET.b2), tw: -28, ht: -20, fF: [FEET.f1, 0], fB: [FEET.b2, 0], rF: 0.5 }), F: A.pCut, B: A.qCut },
  { label: '3発目・右の斬り上げ・振りかぶり', hit: 3, phase: 'wind', side: 'F', dir: 'up', body: W3, F: A.pLow, B: A.qCut },
  { label: '3発目・右の斬り上げ・溜め', hit: 3, phase: 'pull', side: 'F', dir: 'up', body: pull(W3, { py: -57, lean: 20 }), F: A.pLowPull, B: A.qCut },
  { label: '3発目・右の斬り上げ・斬り抜け', hit: 3, phase: 'cut', side: 'F', dir: 'up',
    body: wind({ px: pxOf(FEET.b2, FEET.f3), py: -79, lean: -5, tw: -30, ht: -16, fF: [FEET.f3, 0], fB: [FEET.b2, 0], rF: 0.75, rB: 0.9 }), F: A.pHigh, B: A.qCut },
  { label: '4発目・左の斬り上げ・振りかぶり', hit: 4, phase: 'wind', side: 'B', dir: 'up', body: W4, F: A.pHigh, B: A.qLow },
  { label: '4発目・左の斬り上げ・溜め', hit: 4, phase: 'pull', side: 'B', dir: 'up', body: pull(W4, { py: -57, lean: 20 }), F: A.pHigh, B: A.qLowPull },
  { label: '4発目・左の斬り上げ・斬り抜け', hit: 4, phase: 'cut', side: 'B', dir: 'up',
    body: wind({ px: pxOf(FEET.f3, FEET.b4), py: -79, lean: -5, tw: 30, ht: 16, fF: [FEET.f3, 0], fB: [FEET.b4, 0], rF: 0.9, rB: 0.75 }), F: A.pHigh, B: A.qHigh },
]

const D2R = Math.PI / 180
/** 肩から手（肩からのずれ）へ届く二の腕・前腕の角度（度） */
export function solveArm(hand: Vec, bend: 1 | -1): { a: number; b: number } {
  const L1 = HUMAN_RIG.upper, L2 = HUMAN_RIG.fore
  const d = Math.min(Math.hypot(hand[0], hand[1]), L1 + L2 - 0.01), base = Math.atan2(hand[1], hand[0])
  const off = Math.acos(Math.max(-1, Math.min(1, (L1 * L1 + d * d - L2 * L2) / (2 * L1 * d))))
  const a = base - bend * off, e: Vec = [Math.cos(a) * L1, Math.sin(a) * L1]
  return { a: a / D2R, b: Math.atan2(hand[1] - e[1], hand[0] - e[0]) / D2R }
}

/** 体と2本の腕から姿勢を作る */
export function crossPose(body: Body, F: CrossArm, B: CrossArm): Pose {
  const f = solveArm(F.hand, F.bend), b = solveArm(B.hand, B.bend)
  return humanPose({ ...body, aF: f.a, bF: f.b, sF: F.s, aB: b.a, bB: b.b, sB: B.s })
}

export const crossKeyPose = (k: CrossKey): Pose => crossPose(k.body, k.F, k.B)

const lerp = (a: number, b: number, u: number) => a + (b - a) * u
/** 手が先に斜めへ走り、剣は遅れて最後に返る（手首の返し）。剣が先に回ると剣先が頭の上で大きな弧を描き、斜めの線に見えない */
export const CROSS_SNAP = 2.4
/** 弧で振る時は手が肩の周りを回るので、剣は手より少しだけ遅れればよい */
export const CROSS_ARC_SNAP = 1.4
/** 手の通り道：行き先に via があれば、u＝0.5 で via を通る2次の曲線（肩の前をまっすぐ横切らない） */
function handAt(x: CrossArm, y: CrossArm, u: number): Vec {
  const a = x.hand, b = y.hand
  if (!y.via || x === y) return [lerp(a[0], b[0], u), lerp(a[1], b[1], u)]
  const c: Vec = [2 * y.via[0] - (a[0] + b[0]) / 2, 2 * y.via[1] - (a[1] + b[1]) / 2], v = 1 - u
  return [v * v * a[0] + 2 * u * v * c[0] + u * u * b[0], v * v * a[1] + 2 * u * v * c[1] + u * u * b[1]]
}
/** 斬り上げは剣を遅らせない：下から振り出す時に剣が下を向いたまま手だけ前へ出ると、剣先が床と脚をこすって「く」の字に折れる（11-04 模型のチェック） */
export const CROSS_UP_SNAP = 1
const lerpArm = (x: CrossArm, y: CrossArm, u: number, snap = y.via ? CROSS_ARC_SNAP : CROSS_SNAP): CrossArm => ({ hand: handAt(x, y, u), s: lerp(x.s, y.s, Math.pow(u, snap)), bend: u < 0.5 ? x.bend : y.bend })
/** 床に着いているとみなす高さ（模型の画面の足の印と同じ） */
export const FOOT_PLANTED = -3
/** 足の運び：前後へ動く足は、床に着いている間は動かさず、浮いてから運んで着く前に止める（着いたまま滑らせない・11-04） */
function footAt(a: Vec, b: Vec, u: number): Vec {
  if (Math.abs(b[0] - a[0]) < 0.5) return [lerp(a[0], b[0], u), lerp(a[1], b[1], u)]
  const u0 = a[1] > FOOT_PLANTED ? 0.15 : 0, u1 = b[1] > FOOT_PLANTED ? 0.85 : 1
  const w = Math.max(0, Math.min(1, (u - u0) / (u1 - u0))), s = w * w * (3 - 2 * w)
  return [lerp(a[0], b[0], s), lerp(a[1], b[1], u) - 8 * Math.sin(Math.PI * u)]
}
const lerpBody = (x: Body, y: Body, u: number): Body => ({
  px: lerp(x.px, y.px, u), py: lerp(x.py, y.py, u), lean: lerp(x.lean, y.lean, u), tw: lerp(x.tw, y.tw, u), ht: lerp(x.ht, y.ht, u),
  fF: footAt(x.fF, y.fF, u), fB: footAt(x.fB, y.fB, u),
  rF: lerp(x.rF ?? 0, y.rF ?? 0, u), rB: lerp(x.rB ?? 0, y.rB ?? 0, u),
})

/** 2枚の止め絵の間（u＝0〜1）。手は弧（via）か直線、剣の向きは遅れて返す */
export function crossBetween(a: CrossKey, b: CrossKey, u: number): Pose {
  const up = b.phase === 'cut' && b.dir === 'up' ? CROSS_UP_SNAP : undefined
  return crossPose(lerpBody(a.body, b.body, u), lerpArm(a.F, b.F, u, b.side === 'F' ? up : undefined), lerpArm(a.B, b.B, u, b.side === 'B' ? up : undefined))
}

const SWORD = 58
/** 剣先（体の原点 rx を足した、床の上の位置） */
export function crossTip(p: Pose, side: 'F' | 'B'): Vec {
  const h = p['h' + side] as Vec, s = (p['s' + side] as number) * D2R
  return [h[0] + Math.cos(s) * SWORD + p.rx, h[1] + Math.sin(s) * SWORD]
}

/** n 発目（1〜4）の刃の通り道：溜め→斬り抜けの剣先の点列 */
export function crossStrokePath(hit: number, steps = 16): Vec[] {
  const i = (hit - 1) * 3 + 1, a = BLADE_QUICK_CROSS_KEYS[i]!, b = BLADE_QUICK_CROSS_KEYS[i + 1]!
  return Array.from({ length: steps + 1 }, (_, j) => crossTip(crossBetween(a, b, j / steps), a.side))
}

/** 止め絵を描く台（模型の画面の寸法・キーは止め絵を 100ms ごとに並べただけ） */
export const BLADE_QUICK_CROSS_STILLS: MockupMove = {
  id: 'blade-quick-cross-keys', boss: '剣舞の役者', label: '速い連・左右交互に切りつける止め絵12枚', body: 'human-dual-dress',
  durMs: 1100, scale: 0.8, baseX: 352, heroAt: 192, interp: 'linear',
  keys: BLADE_QUICK_CROSS_KEYS.map((k, i) => ({ at: i * 100, pose: crossKeyPose(k) })),
  phases: [{ from: 0, to: 1100, label: '止め絵', kind: 'rest' }], hits: [], activeMs: 0, box: [22, 100, -150, -40], paths: ['sF', 'sB'],
}
